import api from "./api";
import { withActingInstitute } from "../utils/actingInstituteParam";

/**
 * PII (Personal Information) reveal service.
 *
 * Thin wrapper over the shared axios instance (`services/api.js`, baseURL
 * already includes `/api/v1`) for the two audited "unmask" endpoints of the
 * pii-masking feature. Default responses everywhere are masked; these are the
 * ONLY way to obtain the real (unmasked) PII values, and every successful
 * reveal writes one audit row on the backend.
 *
 * Backend contract (confirmed against source — do NOT add params the endpoints
 * don't accept, per cross-repo-contracts rule):
 *   - Router: MyAMSBackend/routes/v1/piiRevealRoutes.js, mounted at `/pii`.
 *   - Controller: MyAMSBackend/controllers/piiRevealController.js reads ONLY
 *     `req.params.id`. There is NO request body and NO query param — tenant
 *     (institute) context is resolved server-side from the token / acting-
 *     institute middleware, never sent by the client.
 *
 *   revealStudentPii(id):
 *     POST /pii/students/:id/reveal
 *     Guard: ANY of `student.pii.reveal` | `student.pii.reveal_mentee`.
 *     Success: { status, message: "PII revealed", data: { <allowedFields> } }
 *       where allowedFields is the full student PII set (full-reveal) or the
 *       parent-contact subset (GFM mentee). 401/403/404 on failure.
 *
 *   revealTeacherPii(id):
 *     POST /pii/teachers/:id/reveal
 *     Guard: `teacher.pii.reveal` (full reveal only — no mentee path).
 *     Success: { status, message: "PII revealed", data: { <full teacher PII> } }.
 *       401/403/404 on failure.
 *
 * Error handling (no-crash-guarantee, Req 9.3, 9.5): these functions do NOT
 * swallow errors. They return the axios promise so the CALLER wraps the call in
 * try/catch, keeps the values masked on failure, and surfaces a toast. The
 * unmasked result is used in component-local state only and never persisted.
 *
 * Reference: .kiro/specs/pii-masking/design.md (§5 Routes, §9 Frontend);
 * Requirements 9.2, 4.x, 5.x.
 */

const piiService = {
  /**
   * Reveal a single student's PII (full set or parent-contact subset, decided
   * server-side by the caller's permission + mentee relationship).
   *
   * @param {number|string} id - Student record primary key (`:id` path param).
   * @returns {Promise} Axios response promise; caller must try/catch.
   */
  // withActingInstitute threads the Super Admin's selected institute as an
  // `instituteId` query param (dropdown-select flow). Without it the reveal
  // service can't resolve the tenant and returns 401. No-op for other roles.
  revealStudentPii: (id) => api.post(`/pii/students/${id}/reveal`, {}, withActingInstitute()),

  /**
   * Reveal a single teacher's full PII.
   *
   * @param {number|string} id - Teacher record primary key (`:id` path param).
   * @returns {Promise} Axios response promise; caller must try/catch.
   */
  revealTeacherPii: (id) => api.post(`/pii/teachers/${id}/reveal`, {}, withActingInstitute()),

  /**
   * Reveal a single user's PII (email / phoneNumber / dob) for the platform
   * All Users screen. Backend: POST /pii/users/:id/reveal, gated by
   * `user.pii.reveal` (superAdmin holds it implicitly). Success:
   * { status, message: "PII revealed", data: { email, phoneNumber, dob } }.
   *
   * @param {number|string} id - User primary key (`users.id`).
   * @returns {Promise} Axios response promise; caller must try/catch.
   */
  revealUserPii: (id) => api.post(`/pii/users/${id}/reveal`),
};

export const { revealStudentPii, revealTeacherPii, revealUserPii } = piiService;

export default piiService;
